import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { 
  X, 
  Calendar, 
  Clock, 
  User, 
  FileText, 
  Phone, 
  Edit,
  Check,
  Ban,
  PlayCircle
} from 'lucide-react';
import { RendezVous } from '../../types';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface RendezVousDetailsProps {
  rendezVous: RendezVous;
  onClose: () => void;
  onEdit: () => void;
  onCreateConsultation: (rdv: RendezVous) => void;
  onStatusChange: (id: number, status: string) => void;
}

export default function RendezVousDetails({
  rendezVous,
  onClose,
  onEdit,
  onCreateConsultation,
  onStatusChange
}: RendezVousDetailsProps) {
  const getStatusColor = (status: string) => {
    const colors = {
      'attente': 'bg-blue-100 text-blue-800 border-blue-300',
      'confirme': 'bg-green-100 text-green-800 border-green-300',
      'realise': 'bg-gray-100 text-gray-800 border-gray-300',
      'annule': 'bg-red-100 text-red-800 border-red-300',
      'non_honore': 'bg-orange-100 text-orange-800 border-orange-300'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getStatusLabel = (status: string) => {
    const labels = {
      'attente': 'En attente',
      'confirme': 'Confirmé',
      'realise': 'Réalisé',
      'annule': 'Annulé',
      'non_honore': 'Non honoré'
    };
    return labels[status as keyof typeof labels] || status;
  };

  const canBeConfirmed = rendezVous.status === 'attente';
  const canBeCancelled = ['attente', 'confirme'].includes(rendezVous.status);
  const canCreateConsultation = ['confirme', 'attente'].includes(rendezVous.status);

  return (
    <Transition appear show={true} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    Détails du rendez-vous
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="p-6 space-y-6">
                  {/* Statut */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-500">Statut</span>
                    <div className={`px-3 py-1 text-sm font-medium rounded-full border ${getStatusColor(rendezVous.status)}`}>
                      {getStatusLabel(rendezVous.status)}
                    </div>
                  </div>

                  {/* Date et heure */}
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <Calendar className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {format(new Date(rendezVous.date_heure), 'EEEE d MMMM yyyy', { locale: fr })}
                        </p>
                        <p className="text-sm text-gray-500">Date du rendez-vous</p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <Clock className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {format(new Date(rendezVous.date_heure), 'HH:mm')}
                        </p>
                        <p className="text-sm text-gray-500">Heure du rendez-vous</p>
                      </div>
                    </div>
                  </div>

                  {/* Patient */}
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <User className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {rendezVous.patient_details?.nom} {rendezVous.patient_details?.prenoms}
                        </p>
                        <p className="text-sm text-gray-500">Patient</p>
                      </div>
                    </div>

                    {rendezVous.patient_details?.telephone && (
                      <div className="flex items-center space-x-3">
                        <Phone className="h-5 w-5 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {rendezVous.patient_details.telephone}
                          </p>
                          <p className="text-sm text-gray-500">Téléphone</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Médecin */}
                  <div className="flex items-center space-x-3">
                    <User className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {rendezVous.doctor_details?.user_details 
                          ? `Dr. ${rendezVous.doctor_details.user_details.first_name} ${rendezVous.doctor_details.user_details.last_name}`
                          : 'Médecin non assigné'
                        }
                      </p>
                      <p className="text-sm text-gray-500">Médecin</p>
                    </div>
                  </div>

                  {/* Motif */}
                  <div className="flex items-start space-x-3">
                    <FileText className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {rendezVous.motif}
                      </p>
                      <p className="text-sm text-gray-500">Motif de consultation</p>
                    </div>
                  </div>

                  {/* Notes */}
                  {rendezVous.notes && (
                    <div className="flex items-start space-x-3">
                      <FileText className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {rendezVous.notes}
                        </p>
                        <p className="text-sm text-gray-500">Notes</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="px-6 py-4 bg-gray-50 border-t space-y-3">
                  {/* Actions de statut */}
                  <div className="flex flex-wrap gap-2">
                    {canBeConfirmed && (
                      <button
                        onClick={() => {
                          onStatusChange(rendezVous.id, 'confirme');
                          onClose();
                        }}
                        className="flex items-center px-3 py-2 text-sm font-medium text-green-700 bg-green-100 rounded-md hover:bg-green-200 transition-colors"
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Confirmer
                      </button>
                    )}

                    {canBeCancelled && (
                      <button
                        onClick={() => {
                          onStatusChange(rendezVous.id, 'annule');
                          onClose();
                        }}
                        className="flex items-center px-3 py-2 text-sm font-medium text-red-700 bg-red-100 rounded-md hover:bg-red-200 transition-colors"
                      >
                        <Ban className="h-4 w-4 mr-1" />
                        Annuler
                      </button>
                    )}

                    {canCreateConsultation && (
                      <button
                        onClick={() => {
                          onCreateConsultation(rendezVous);
                          onClose();
                        }}
                        className="flex items-center px-3 py-2 text-sm font-medium text-purple-700 bg-purple-100 rounded-md hover:bg-purple-200 transition-colors"
                      >
                        <PlayCircle className="h-4 w-4 mr-1" />
                        Consultation
                      </button>
                    )}
                  </div>

                  {/* Actions principales */}
                  <div className="flex space-x-3 pt-2 border-t">
                    <button
                      onClick={onEdit}
                      className="flex-1 flex items-center justify-center px-4 py-2 text-sm font-medium text-blue-700 bg-blue-100 rounded-md hover:bg-blue-200 transition-colors"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Modifier
                    </button>

                    <button
                      onClick={onClose}
                      className="flex-1 flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                    >
                      Fermer
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}